import { useCallback, useEffect, useMemo, useState } from "react";
import { t, getLang } from "@/lib/i18n";
import { MapPin, LocateFixed, RefreshCw, Search, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import NewsCard, { timeAgo } from "@/components/NewsCard";
import { Skeleton } from "@/components/ui/skeleton";

const SCOPES = [
  { key: "near", label: "Near me" }, { key: "jaipur", label: "Jaipur" }, { key: "rajasthan", label: "Rajasthan" },
  { key: "india", label: "India" }, { key: "global", label: "Global" },
];
const TOPICS = [
  { key: "all", label: "All updates" }, { key: "penalties", label: "Fines & govt orders" }, { key: "civic", label: "Civic & municipal" },
  { key: "recycling", label: "Recycling & scrap" }, { key: "plastic", label: "Plastic" }, { key: "climate", label: "Climate & air" },
  { key: "waste_workers", label: "Kabadiwalas & waste workers" }, { key: "policy", label: "Policy & rules" },
];
const PERIODS = [{ key: 1, label: "24h" }, { key: 7, label: "7 days" }, { key: 30, label: "30 days" }];
const PREF_KEY = "ks_news_prefs";

const loadPrefs = () => {
  try { return { scope: "jaipur", topics: ["all"], days: 30, city: "", ...JSON.parse(localStorage.getItem(PREF_KEY) || "{}") }; }
  catch { return { scope: "jaipur", topics: ["all"], days: 30, city: "" }; }
};

function dayLabel(iso) {
  if (!iso) return "Earlier";
  const d = new Date(iso); const today = new Date();
  const diff = Math.floor((new Date(today.toDateString()) - new Date(d.toDateString())) / 86400000);
  if (diff === 0) return "Today";
  if (diff === 1) return "Yesterday";
  return d.toLocaleDateString(getLang() === "hi" ? "hi-IN" : "en-IN", { weekday: "long", day: "numeric", month: "long" });
}

const Pill = ({ active, onClick, children, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors whitespace-nowrap ${active ? "bg-[var(--ink)] text-white border-[var(--ink)]" : "bg-white border-[rgba(20,40,26,.12)] text-[var(--ink-soft)] hover:border-[var(--leaf)] hover:text-[var(--ink)]"}`}>
    {children}
  </button>
);

export default function News() {
  const [prefs, setPrefs] = useState(loadPrefs);
  const [q, setQ] = useState("");
  const [query, setQuery] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [locating, setLocating] = useState(false);
  const [cityInput, setCityInput] = useState("");

  useEffect(() => { localStorage.setItem(PREF_KEY, JSON.stringify(prefs)); }, [prefs]);

  const load = useCallback(async () => {
    if (prefs.scope === "near" && !prefs.city) return;
    setLoading(true);
    try {
      const { data } = await api.get("/news/feed", { params: { scope: prefs.scope, topics: prefs.topics.join(","), days: prefs.days, city: prefs.city || undefined, q: query || undefined, limit: 80 } });
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
      setData({ items: [] });
    } finally {
      setLoading(false);
    }
  }, [prefs, query]);

  useEffect(() => { load(); }, [load]);

  const locate = () => {
    if (!navigator.geolocation) return toast.error(t("Location isn't available on this device. Type your city instead."));
    setLocating(true);
    navigator.geolocation.getCurrentPosition(async (pos) => {
      try {
        const { data } = await api.get("/geo/locate", { params: { lat: pos.coords.latitude, lng: pos.coords.longitude } });
        if (!data.city) throw new Error("City not found");
        setPrefs((p) => ({ ...p, scope: "near", city: data.city, state: data.state }));
        toast.success(`${t("Showing news near")} ${data.city}`);
      } catch (e) { toast.error(t("Couldn't detect your city. Please type it below.")); }
      finally { setLocating(false); }
    }, () => { setLocating(false); toast.error(t("Location permission denied. You can type your city instead.")); }, { timeout: 10000 });
  };

  const pickScope = (key) => {
    if (key === "near" && !prefs.city) { setPrefs((p) => ({ ...p, scope: "near" })); return locate(); }
    setPrefs((p) => ({ ...p, scope: key }));
  };

  const toggleTopic = (key) => setPrefs((p) => {
    if (key === "all") return { ...p, topics: ["all"] };
    const set = new Set(p.topics.filter((tp) => t !== "all"));
    set.has(key) ? set.delete(key) : set.add(key);
    return { ...p, topics: set.size ? [...set] : ["all"] };
  });

  const setManualCity = (e) => {
    e.preventDefault();
    const c = cityInput.trim();
    if (c.length < 2) return;
    setPrefs((p) => ({ ...p, scope: "near", city: c, state: "" }));
    setCityInput("");
  };

  const groups = useMemo(() => {
    const out = [];
    (data?.items || []).forEach((it) => {
      const label = dayLabel(it.published_iso);
      const last = out[out.length - 1];
      last && last.label === label ? last.items.push(it) : out.push({ label, items: [it] });
    });
    return out;
  }, [data]);

  let idx = 0;
  return (
    <div data-testid="news-page">
      <PageHeader testid="news-header" eyebrow="Local news" title={t("What's happening around you.")}
        subtitle="Civic updates, government orders and fines, recycling, plastic and climate news — newest first. Choose your area and topics; we'll remember your choice on this device." />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-3xl border border-[rgba(20,40,26,.08)] p-4 sm:p-6 space-y-5" data-testid="news-filters">
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-[var(--ink-soft)] mb-2.5">{t("Area")}</div>
            <div className="flex gap-2 overflow-x-auto pb-1">
              {SCOPES.map((s) => (
                <Pill key={s.key} active={prefs.scope === s.key} onClick={() => pickScope(s.key)} testid={`news-scope-${s.key}`}>
                  {s.key === "near" && <LocateFixed size={14} className="inline mr-1.5 -mt-0.5" />}{t(s.label)}
                </Pill>
              ))}
            </div>
            {prefs.scope === "near" && (
              <div className="mt-3 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="news-near-controls">
                {prefs.city && (
                  <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[var(--leaf-soft)] text-[var(--leaf-dark)] text-sm font-semibold" data-testid="news-city-chip">
                    <MapPin size={14} /> {prefs.city}{prefs.state ? `, ${prefs.state}` : ""}
                  </span>
                )}
                <button onClick={locate} disabled={locating} className="inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--leaf-dark)] link-u" data-testid="news-detect-location">
                  {locating ? <Loader2 size={14} className="animate-spin" /> : <LocateFixed size={14} />} {locating ? t("Detecting…") : t("Use my GPS location")}
                </button>
                <form onSubmit={setManualCity} className="flex gap-2">
                  <input value={cityInput} onChange={(e) => setCityInput(e.target.value)} placeholder={t("or type a city / area")} data-testid="news-city-input"
                    className="px-3.5 py-2 rounded-full border border-[rgba(20,40,26,.12)] text-sm bg-[var(--paper)] focus:outline-none focus:ring-2 focus:ring-[var(--leaf)]/30" />
                  <button className="px-4 py-2 rounded-full bg-[var(--leaf)] text-white text-sm font-semibold" data-testid="news-city-submit">{t("Set")}</button>
                </form>
              </div>
            )}
          </div>

          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-[var(--ink-soft)] mb-2.5">{t("Topics")} <span className="normal-case font-medium">{t("(pick one or more)")}</span></div>
            <div className="flex flex-wrap gap-2">
              {TOPICS.map((tp) => <Pill key={tp.key} active={prefs.topics.includes(tp.key)} onClick={() => toggleTopic(tp.key)} testid={`news-topic-${tp.key}`}>{t(tp.label)}</Pill>)}
            </div>
          </div>

          <div className="flex flex-col md:flex-row md:items-center gap-4 justify-between">
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold uppercase tracking-wider text-[var(--ink-soft)] mr-1">{t("Period")}</span>
              {PERIODS.map((p) => <Pill key={p.key} active={prefs.days === p.key} onClick={() => setPrefs((x) => ({ ...x, days: p.key }))} testid={`news-period-${p.key}`}>{t(p.label)}</Pill>)}
            </div>
            <form onSubmit={(e) => { e.preventDefault(); setQuery(q.trim()); }} className="flex items-center gap-2 w-full md:w-auto">
              <div className="relative flex-1 md:w-72">
                <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--ink-soft)]" />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("Search e.g. Malviya Nagar, e-waste")} data-testid="news-search-input"
                  className="w-full pl-9 pr-8 py-2.5 rounded-full border border-[rgba(20,40,26,.12)] text-sm bg-[var(--paper)] focus:outline-none focus:ring-2 focus:ring-[var(--leaf)]/30" />
                {query && <button type="button" onClick={() => { setQ(""); setQuery(""); }} className="absolute right-3 top-1/2 -translate-y-1/2" aria-label="Clear search" data-testid="news-search-clear"><X size={14} /></button>}
              </div>
              <button className="btn-leaf !py-2.5 !px-4 text-sm" data-testid="news-search-submit">{t("Search")}</button>
            </form>
          </div>
        </div>

        <div className="flex items-center justify-between mt-8 mb-4">
          <div className="text-sm text-[var(--ink-soft)]" data-testid="news-result-meta">
            {data ? <>{data.count} {t("stories")} · <b className="text-[var(--ink)]">{data.scope_label}</b> · {t("newest first")} · {t("updated")} {timeAgo(data.fetched_at)}</> : prefs.scope === "near" && !prefs.city ? t("Allow location or type your city to see nearby news.") : t("Loading…")}
          </div>
          <button onClick={load} disabled={loading} className="inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--leaf-dark)]" data-testid="news-refresh-btn">
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> {t("Refresh")}
          </button>
        </div>

        {loading && !data ? (
          <div className="grid md:grid-cols-2 gap-4">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-40 rounded-2xl" />)}</div>
        ) : groups.length === 0 ? (
          <div className="bg-white rounded-3xl border border-dashed border-[rgba(20,40,26,.2)] p-10 text-center text-[var(--ink-soft)]" data-testid="news-empty">
            {t("No stories found for these filters. Try a longer period or fewer topics.")}
          </div>
        ) : (
          <div className={`space-y-10 transition-opacity ${loading ? "opacity-50" : ""}`} data-testid="news-list">
            {groups.map((g) => (
              <div key={t(g.label)}>
                <div className="sticky top-16 sm:top-20 z-10 py-2 bg-[var(--paper)]/90 backdrop-blur">
                  <h2 className="text-base md:text-lg font-bold flex items-center gap-3">{t(g.label)}<span className="flex-1 h-px bg-[rgba(20,40,26,.1)]" /></h2>
                </div>
                <div className="mt-3 grid md:grid-cols-2 gap-4">
                  {g.items.map((it) => <NewsCard key={it.link} item={t(it)} index={idx++} />)}
                </div>
              </div>
            ))}
          </div>
        )}
        <p className="mt-10 text-xs text-[var(--ink-soft)]">{t("Headlines are sourced automatically from Google News and link to the original publishers. Kindsaver does not write or endorse these stories.")}</p>
      </section>
    </div>
  );
}
